import { createElement, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";

import "./alert.scss";

export const ALERT_SLOT_ROLES = {
  "Alert-UI-1": [],
  "Alert-UI-2": ["Primary"],
  "Alert-UI-3": ["Secondary", "Primary"],
  "Alert-UI-4": ["Primary", "Destructive", "Secondary"],
  "Alert-UI-5": ["Secondary", "Destructive"],
  "Alert-UI-6": ["Secondary", "Destructive"],
  "Alert-UI-7": ["Cancel"],
  "Alert-UI-8": ["Cancel"],
};

// Whether a variant renders a title row at all. A property of the VARIANT, not of the
// message — see ADR-0011. Exhaustive on purpose (not a set of title-less exceptions): an
// unlisted new variant reads as falsy and hides its title, which surfaces immediately in
// the authoring form rather than silently defaulting.
export const ALERT_VARIANT_HAS_TITLE = {
  "Alert-UI-1": true,
  "Alert-UI-2": true,
  "Alert-UI-3": true,
  "Alert-UI-4": true,
  "Alert-UI-5": true,
  "Alert-UI-6": true,
  // Text + loader + Cancel, nothing else. The only title-less variant; this flag is the
  // only thing distinguishing UI-7 from UI-8, which is otherwise identical.
  "Alert-UI-7": false,
  "Alert-UI-8": true,
};

const VARIANT_LAYOUT = {
  "Alert-UI-1": "none",
  "Alert-UI-2": "single",
  "Alert-UI-3": "row",
  "Alert-UI-4": "stack",
  "Alert-UI-5": "row",
  "Alert-UI-6": "row-soft",
  "Alert-UI-7": "single",
  "Alert-UI-8": "single",
};

// Variants that render a loading spinner (between the message and the buttons).
const VARIANT_LOADING = {
  "Alert-UI-7": true,
  "Alert-UI-8": true,
};

const FOCUSABLE_SELECTOR = [
  "button:not(:disabled)",
  "[href]",
  "input:not(:disabled)",
  "select:not(:disabled)",
  "textarea:not(:disabled)",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const isDev = (() => {
  const proc = typeof globalThis !== "undefined" ? globalThis.process : undefined;
  return Boolean(proc && proc.env && proc.env.NODE_ENV !== "production");
})();

const warn = (msg) => {
  if (isDev && typeof console !== "undefined") console.warn(`[Alert] ${msg}`);
};

const resolveVariant = (variant) => {
  if (ALERT_SLOT_ROLES[variant]) return variant;
  warn(`Unknown variant "${variant}"; falling back to "Alert-UI-1".`);
  return "Alert-UI-1";
};

export function alert(props) {
  return new Promise((resolve) => {
    const container = document.createElement("div");
    document.body.appendChild(container);
    const root = createRoot(container);

    const cleanup = () => {
      setTimeout(() => {
        root.unmount();
        container.remove();
      }, 10);
    };

    const resolveWith = (result) => {
      cleanup();
      resolve(result);
    };

    const roles = ALERT_SLOT_ROLES[props?.variant] ?? [];
    const handlers = {
      onClose: () => resolveWith(null),
    };
    roles.forEach((role, index) => {
      handlers[`on${role}`] = (label) => resolveWith({ label, role, index });
    });

    root.render(createElement(Alert, { ...props, ...handlers }));
  });
}

export default function Alert({
  variant,
  title,
  message,
  buttons,
  onPrimary,
  onDestructive,
  onSecondary,
  onCancel,
  onClose,
  dismissible = false,
  inline = false,
}) {
  const safeVariant = resolveVariant(variant);
  const hasTitle = ALERT_VARIANT_HAS_TITLE[safeVariant] !== false;
  const roles = ALERT_SLOT_ROLES[safeVariant];
  const layout = VARIANT_LAYOUT[safeVariant];
  const required = roles.length;

  const cardRef = useRef(null);
  const lastFocusedRef = useRef(null);

  const incoming = Array.isArray(buttons) ? buttons : [];
  if (isDev && incoming.length !== required) {
    warn(
      `Variant ${safeVariant} requires ${required} button${required === 1 ? "" : "s"}, got ${incoming.length}.`,
    );
  }
  const safeButtons = incoming.slice(0, required);

  // UI-1 is always dismissible; other variants depend on the dismissible prop.
  // Inline mode opts out of all dismissibility chrome (Esc, backdrop).
  const canDismiss = !inline && (safeVariant === "Alert-UI-1" || Boolean(dismissible));

  useEffect(() => {
    if (!isDev) return;
    const provided = {
      Primary: onPrimary,
      Destructive: onDestructive,
      Secondary: onSecondary,
      Cancel: onCancel,
    };
    Object.entries(provided).forEach(([role, handler]) => {
      if (handler && !roles.includes(role)) {
        warn(
          `Variant ${safeVariant} has no ${role} slot; on${role} handler will not fire.`,
        );
      }
      if (!handler && roles.includes(role)) {
        warn(
          `Variant ${safeVariant} has a ${role} slot but no on${role} handler was provided.`,
        );
      }
    });
  }, [safeVariant, roles, onPrimary, onDestructive, onSecondary, onCancel]);

  useEffect(() => {
    if (!isDev) return;
    if (!hasTitle && title != null && title !== "") {
      warn(`Variant ${safeVariant} has no title row; the "title" prop is ignored.`);
    }
  }, [hasTitle, safeVariant, title]);

  useEffect(() => {
    if (inline) return;
    lastFocusedRef.current = document.activeElement;

    const card = cardRef.current;
    if (!card) return;

    const initialRole = roles.includes("Secondary")
      ? "Secondary"
      : roles.length > 0
        ? roles[0]
        : "Close";

    const initialEl =
      initialRole === "Close"
        ? card.querySelector('[data-alert-close="true"]')
        : card.querySelector(`[data-alert-role="${initialRole}"]`);

    if (initialEl) initialEl.focus();
    else card.focus();

    return () => {
      const prev = lastFocusedRef.current;
      if (prev && typeof prev.focus === "function") prev.focus();
    };
  }, [roles, inline]);

  useEffect(() => {
    if (inline) return;
    const card = cardRef.current;
    if (!card) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (canDismiss) {
          e.preventDefault();
          onClose?.();
        } else {
          e.preventDefault();
          e.stopPropagation();
        }
        return;
      }
      if (e.key !== "Tab") return;
      const focusables = Array.from(card.querySelectorAll(FOCUSABLE_SELECTOR));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [canDismiss, onClose, inline]);

  const slotHandler = (role) => {
    if (role === "Primary") return onPrimary;
    if (role === "Destructive") return onDestructive;
    if (role === "Secondary") return onSecondary;
    if (role === "Cancel") return onCancel;
    return undefined;
  };

  const handleButtonClick = (role, label) => {
    const handler = slotHandler(role);
    if (handler) handler(label);
    else warn(`Variant ${safeVariant} ${role} button clicked but no on${role} handler was provided.`);
  };

  const handleBackdrop = () => {
    if (canDismiss) onClose?.();
  };

  // A title-less variant has no title element, so the message element takes over as the
  // accessible name — an `aria-labelledby` aimed at an absent (or empty) title element
  // would leave `role="alertdialog"` anonymous, which is worse than having no title at
  // all. `title != null` alone was not enough: a stored empty string is not null.
  const titleShown = hasTitle && title != null && title !== "";
  const labelledBy = inline ? undefined : titleShown ? "alert-title" : message != null ? "alert-message" : undefined;
  // Only describe by the message when something else is naming the dialog; otherwise the
  // same element would be announced twice.
  const describedBy = !inline && titleShown && message != null ? "alert-message" : undefined;

  const card = (
    <div
      className={`alert-card${inline ? " alert-card--inline" : ""}`}
      role={inline ? undefined : "alertdialog"}
      aria-modal={inline ? undefined : "true"}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      ref={cardRef}
      tabIndex={inline ? undefined : -1}
      onClick={inline ? undefined : (e) => e.stopPropagation()}
    >
      {hasTitle && (
        <div className="alert-title-row">
          <div className="alert-title" id={inline ? undefined : "alert-title"}>
            {title}
          </div>
          {safeVariant === "Alert-UI-1" && (
            <button
              type="button"
              className="alert-close-link"
              data-alert-close="true"
              onClick={() => onClose?.()}
            >
              Close
            </button>
          )}
        </div>
      )}
      {message != null && (
        <div className="alert-message" id={inline ? undefined : "alert-message"}>
          {message}
        </div>
      )}
      {VARIANT_LOADING[safeVariant] && (
        <div className="alert-spinner" role="status" aria-label="Loading">
          <span className="alert-spinner-ring" />
        </div>
      )}
      {required > 0 && (
        <div className={`alert-buttons alert-buttons--${layout}`}>
          {roles.map((role, idx) => {
            const btn = safeButtons[idx];
            const label = btn?.label ?? "";
            return (
              <button
                key={`${role}-${idx}`}
                type="button"
                className={`alert-btn alert-btn--${role.toLowerCase()}`}
                data-alert-role={role}
                onClick={() => handleButtonClick(role, label)}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  if (inline) return card;

  return (
    <div className="alert-backdrop" onClick={handleBackdrop}>
      {card}
    </div>
  );
}
